import * as React from "react";
import { Grid, Paper, Stack } from "@mui/material";
import styled from "styled-components";
import LoadingAndInactiveDataHandler from "src/components/loading-and-inactive-data-handler";
import ScrappingPlaceholderSection from "src/scenes/dashboard/scrapping-placeholder-section";
import TemplateItem from "src/components/template/template-item";
import type { Template, TemplateDashboard } from "src/components/template/types";
import TemplateRadioGroup from "src/components/template/template-radio-group";
import TemplateSwitch from "src/components/template/template-switch";
import { IntegrationCategories } from "src/components/template/constants";
import fetchStatus from "src/utils/api/fetch-status";
import type { TeamApi } from "src/scenes/teams/team/types";
import useTemplateDataApi from "src/hooks/use-template-data-api";
import { getIsTemplateNotIntegrated } from "src/utils/is-not-integrated";
import { useActiveIntegrationsTeams } from "src/scenes/teams/dashboard/active-integrations-provider-teams";
import { useActiveIntegrations } from "src/scenes/dashboard/active-integrations-provider";
import TemplateNoIntegration from "src/components/template/template-no-integration";
import TemplateItemEditOverlay from "src/components/template/template-item-edit-overlay";
import TemplateAddRow from "src/components/template/template-add-row";
import TemplateRemoveRow from "src/components/template/template-remove-row";
import { useTemplateDrawer } from "src/components/providers/template-drawer-provider";
import JiraProjectSelector from "src/components/template/jira-project-selector";

const Card = styled(Paper)`
    ${({ big, minHeight }) => {
        if (big) return `height: 17rem`;
        if (minHeight) return `min-height: ${minHeight}`;
        return `min-height: 120px`;
    }};
    border-radius: ${({ top, bottom, right, left }) => {
        const br: string[] = ["0", "0", "0", "0"];
        if (top) {
            if (left) {
                br[0] = "14px";
            }
            if (right) {
                br[1] = "14px";
            }
        }
        if (bottom) {
            if (left) {
                br[2] = "14px";
            }
            if (right) {
                br[3] = "14px";
            }
        }
        return br.join(" ");
    }};
    ${({ theme }) => theme.breakpoints.down("md")} {
        border-radius: 14px 14px 0 0;
    }
`;

const checkGitScrapping = (toolScrapping: string[]): string | undefined => {
    if (toolScrapping.includes("gitlab") || toolScrapping.includes("gitlabSelfManaged")) {
        return "gitlab";
    }
    if (toolScrapping.includes("github")) {
        return "github";
    }
    return undefined;
};

const checkPmScrapping = (toolScrapping: string[]): string | undefined => {
    if (toolScrapping.includes("jira")) {
        return "jira";
    }
    if (toolScrapping.includes("notion")) {
        return "notion";
    }
    return undefined;
};

const checkCollabScrapping = (toolScrapping: string[]): string | undefined => {
    if (toolScrapping.includes("slack")) {
        return "slack";
    }
    if (toolScrapping.includes("google")) {
        return "google";
    }
    if (toolScrapping.includes("microsoft")) {
        return "microsoft";
    }
    return undefined;
};

const checkWellbeingScrapping = (toolScrapping: string[]): string | undefined => {
    return checkGitScrapping(toolScrapping) || checkCollabScrapping(toolScrapping);
};

const checkScrapping = (toolScrapping: string[], dependencies: IntegrationCategories[]) => {
    if (dependencies.includes(IntegrationCategories.git)) {
        const scrapping = checkGitScrapping(toolScrapping);
        if (scrapping) return scrapping;
    }
    if (dependencies.includes(IntegrationCategories.pm)) {
        const scrapping = checkPmScrapping(toolScrapping);
        if (scrapping) return scrapping;
    }
    if (dependencies.includes(IntegrationCategories.collaboration)) {
        const scrapping = checkCollabScrapping(toolScrapping);
        if (scrapping) return scrapping;
    }
    if (dependencies.includes(IntegrationCategories.wellbeing)) {
        const scrapping = checkWellbeingScrapping(toolScrapping);
        if (scrapping) return scrapping;
    }
    return undefined;
};

const Template = ({
    viewType,
    teamId,
    teamApi,
    repos,
    groups,
    toolsScrapping,
    template,
    templateIndex,
    defaultDateRange,
    setTemplateNoData,
    setEarliestFirstScrapped,
    setApiStatus,
    onEditMode,
}: {
    viewType: "user" | "team" | "repo";
    teamId?: string;
    teamApi?: TeamApi;
    repos?: string[];
    groups?: string[];
    toolsScrapping: string[];
    template: Template;
    templateIndex: number;
    defaultDateRange: TemplateDashboard["defaultDateRange"];
    setTemplateNoData: React.Dispatch<React.SetStateAction<string | boolean | null>>;
    setEarliestFirstScrapped: (date: string) => void;
    setApiStatus: (status: fetchStatus | null, code: number | null) => void;
    onEditMode: boolean;
}) => {
    const [queryVariableValue, setQueryVariableValue] = React.useState<string>(
        localStorage.getItem(teamId ? "creator-assignee-teams" : "creator-assignee") || "assignee"
    );
    const [queryVariableProjectValue, setQueryVariableProjectValue] = React.useState<string>(
        localStorage.getItem("project") || "All"
    );

    const [propertyValue, setPropertyValue] = React.useState<string>("issues");
    const { setDrawerOpen } = useTemplateDrawer();

    const context = React.useMemo(() => {
        if (propertyValue && template.propertySetter) {
            return { [template.propertySetter.id]: propertyValue };
        }
        return {};
    }, [propertyValue]);

    const { isIntegrated, isCentrallyManaged, isInvalid } = (
        viewType === "team" ? useActiveIntegrationsTeams : useActiveIntegrations
    )();

    const variables: { "creator-assignee"?: string; project?: string } = {};

    if (template?.queryVariablesSetter && Object.keys(template?.queryVariablesSetter).length > 0) {
        if (template.queryVariablesSetter["creator-assignee"]) {
            variables["creator-assignee"] = queryVariableValue;
        }
        if (template.queryVariablesSetter.project) {
            variables.project = queryVariableProjectValue;
        }
    }

    const fetchAction = useTemplateDataApi({
        template,
        defaultDateRange,
        teamId,
        repos,
        groups,
        variables,
    });

    React.useEffect(() => {
        setTemplateNoData(
            fetchAction &&
                fetchAction.data &&
                fetchAction.data.meta &&
                fetchAction.data.meta.firstScrappedDate &&
                !fetchAction.data.meta.lastActiveAt
        );
    }, [
        fetchAction &&
            fetchAction.data &&
            fetchAction.data.meta &&
            fetchAction.data.meta.firstScrappedDate &&
            fetchAction.data.meta.lastActiveAt,
    ]);

    React.useEffect(() => {
        if (
            fetchAction &&
            fetchAction.data &&
            fetchAction.data.meta &&
            fetchAction.data.meta.firstScrappedDate
        ) {
            setEarliestFirstScrapped(fetchAction.data.meta.firstScrappedDate);
        }
    }, [
        fetchAction &&
            fetchAction.data &&
            fetchAction.data.meta &&
            fetchAction.data.meta.firstScrappedDate,
    ]);

    React.useEffect(() => {
        setApiStatus(fetchAction && fetchAction.status, fetchAction && fetchAction.errorCode);
    }, [fetchAction && fetchAction.status, fetchAction && fetchAction.errorCode]);

    React.useEffect(() => {
        if (!onEditMode) setDrawerOpen(false);
    }, [onEditMode]);

    const notIntegrated = React.useMemo(
        () =>
            getIsTemplateNotIntegrated(
                template,
                viewType === "repo"
                    ? {
                          github: isIntegrated.github || isCentrallyManaged?.github,
                          gitlab: isIntegrated.gitlab || isCentrallyManaged?.gitlab,
                          gitlabSelfManaged:
                              isIntegrated.gitlabSelfManaged ||
                              isCentrallyManaged?.gitlabSelfManaged,
                          slack: isIntegrated.slack || isCentrallyManaged?.slack,
                          google: isIntegrated.google || isCentrallyManaged?.google,
                          jira: isIntegrated.jira || isCentrallyManaged?.jira,
                          notion: isIntegrated.notion || isCentrallyManaged?.notion,
                          microsoft: isIntegrated.microsoft || isCentrallyManaged?.microsoft,
                      }
                    : isIntegrated
            ),
        [template, viewType, isIntegrated]
    );

    if (
        notIntegrated &&
        notIntegrated.length &&
        template?.dependencies &&
        template?.dependencies.length === notIntegrated.length
    ) {
        return <TemplateNoIntegration integrationCategories={notIntegrated} />;
    }

    const scrapping = checkScrapping(toolsScrapping, template.dependencies);
    const isIntegrationInvalid = React.useMemo(() => {
        let temp = false;
        if (!template.dependencies || !template.dependencies.length) return temp;
        if (template.dependencies.includes(IntegrationCategories.git)) {
            temp =
                temp ||
                (isIntegrated.gitlab && isInvalid.gitlab) ||
                (isIntegrated.github && isInvalid.github);
        }
        if (template.dependencies.includes(IntegrationCategories.pm)) {
            temp =
                temp ||
                (isIntegrated.jira && isInvalid.jira) ||
                (isIntegrated.notion && isInvalid.notion);
        }
        return temp;
    }, [isInvalid, template.dependencies]);
    if (!teamApi && scrapping) return <ScrappingPlaceholderSection variant={scrapping} />;
    const dataHandler = LoadingAndInactiveDataHandler({
        name: template.title.value,
        fetchAction,
        isIntegrationInvalid,
    });
    if (dataHandler) return dataHandler;

    const getRows = (initialRowNumber: number = 0) => {
        return (
            <>
                {onEditMode && (
                    <div
                        style={{
                            position: "relative",
                            ...(!template.rows.length && { marginBottom: "50px" }),
                        }}
                    >
                        <TemplateAddRow top position={0} templateIndex={templateIndex} />
                    </div>
                )}
                {template.rows.map((row, rowIndex) => {
                    let rowTotalSize = 0;
                    return (
                        <Grid
                            container={!row.singleCard}
                            item={row.singleCard}
                            direction="row"
                            spacing={1}
                            pt={rowIndex > 0 ? 1 : 0}
                            sx={{ position: "relative" }}
                        >
                            {onEditMode && (
                                <TemplateRemoveRow
                                    position={rowIndex}
                                    templateIndex={templateIndex}
                                />
                            )}
                            {onEditMode && (
                                <TemplateAddRow
                                    top={false}
                                    position={rowIndex + 1}
                                    templateIndex={templateIndex}
                                />
                            )}
                            {row.singleCard ? (
                                <Card
                                    top={rowIndex + initialRowNumber === 0}
                                    bottom={rowIndex === template.rows.length - 1}
                                    left
                                    right
                                >
                                    <Grid container direction="row" mr={3}>
                                        {row.items.map((item, itemIndex) => (
                                            <TemplateItem
                                                viewType={viewType}
                                                item={item}
                                                fetchAction={fetchAction}
                                                teamApi={teamApi}
                                                key={item.title.value}
                                                rowIndex={rowIndex}
                                                itemIndex={itemIndex}
                                                templateIndex={templateIndex}
                                                itemCount={row.items.length - 1}
                                                top={rowIndex + initialRowNumber === 0}
                                                bottom={rowIndex === template.rows.length - 1}
                                                context={context}
                                            />
                                        ))}
                                        {onEditMode && (
                                            <Stack
                                                direction="column"
                                                mt={1}
                                                ml={3}
                                                sx={{
                                                    position: "relative",
                                                    minWidth: "120px",
                                                    minHeight: "98px",
                                                }}
                                            >
                                                <TemplateItemEditOverlay
                                                    isChart={false}
                                                    item={{
                                                        sizing: {
                                                            md: 2,
                                                            xs: 12,
                                                        },
                                                    }}
                                                    rowIndex={rowIndex}
                                                    itemIndex={row.items.length}
                                                    templateIndex={templateIndex}
                                                />
                                            </Stack>
                                        )}
                                    </Grid>
                                </Card>
                            ) : (
                                <>
                                    {row.items.map((item, itemIndex) => {
                                        let beforeComponent: React.ReactNode | null = null;
                                        let afterComponent: React.ReactNode | null = null;
                                        if ("sizing" in item) {
                                            if (
                                                rowTotalSize <= 12 - 3 &&
                                                rowTotalSize + item.sizing.md > 12
                                            ) {
                                                beforeComponent = (
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        md={12 - rowTotalSize}
                                                        sx={{
                                                            position: "relative",
                                                            minHeight: "120px",
                                                        }}
                                                    >
                                                        <TemplateItemEditOverlay
                                                            isChart
                                                            item={{
                                                                sizing: {
                                                                    md: 12 - rowTotalSize,
                                                                    xs: 12,
                                                                },
                                                            }}
                                                            rowIndex={rowIndex}
                                                            itemIndex={itemIndex}
                                                            templateIndex={templateIndex}
                                                        />
                                                    </Grid>
                                                );
                                            }
                                            if (rowTotalSize + item.sizing.md > 12) {
                                                rowTotalSize = item.sizing.md;
                                            } else {
                                                rowTotalSize += item.sizing.md;
                                            }
                                            if (
                                                itemIndex === row.items.length - 1 &&
                                                rowTotalSize < 12
                                            ) {
                                                afterComponent = (
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        md={12 - rowTotalSize}
                                                        sx={{
                                                            position: "relative",
                                                            minHeight: "120px",
                                                        }}
                                                    >
                                                        <TemplateItemEditOverlay
                                                            isChart
                                                            item={{
                                                                sizing: {
                                                                    md: 12 - rowTotalSize,
                                                                    xs: 12,
                                                                },
                                                            }}
                                                            rowIndex={rowIndex}
                                                            itemIndex={row.items.length}
                                                            templateIndex={templateIndex}
                                                        />
                                                    </Grid>
                                                );
                                            }
                                        }
                                        return (
                                            <>
                                                {onEditMode && beforeComponent}
                                                <TemplateItem
                                                    viewType={viewType}
                                                    item={item}
                                                    fetchAction={fetchAction}
                                                    teamApi={teamApi}
                                                    key={item.title.value}
                                                    rowIndex={rowIndex}
                                                    itemIndex={itemIndex}
                                                    templateIndex={templateIndex}
                                                    itemCount={row.items.length - 1}
                                                    top={rowIndex + initialRowNumber === 0}
                                                    bottom={rowIndex === template.rows.length - 1}
                                                    context={context}
                                                />
                                                {onEditMode && afterComponent}
                                            </>
                                        );
                                    })}

                                    {!row.items.length && onEditMode && (
                                        <Grid
                                            item
                                            xs={12}
                                            md={12}
                                            sx={{
                                                position: "relative",
                                                minHeight: "120px",
                                            }}
                                        >
                                            <TemplateItemEditOverlay
                                                isChart
                                                item={{
                                                    sizing: {
                                                        md: 12,
                                                        xs: 12,
                                                    },
                                                }}
                                                rowIndex={rowIndex}
                                                itemIndex={row.items.length}
                                                templateIndex={templateIndex}
                                            />
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Grid>
                    );
                })}
            </>
        );
    };

    const getSection = () => {
        if (template.queryVariablesSetter || template.propertySetter) {
            return (
                <>
                    <Grid item pb={1}>
                        <Card top left right minHeight="41px">
                            <Grid
                                container
                                alignItems="center"
                                justifyContent="space-between"
                                p={1}
                                pl={2}
                                pr={2}
                            >
                                {template.queryVariablesSetter &&
                                    template.queryVariablesSetter["creator-assignee"] && (
                                        <Grid item>
                                            <TemplateRadioGroup
                                                id={
                                                    teamId
                                                        ? "creator-assignee-teams"
                                                        : "creator-assignee"
                                                }
                                                value={queryVariableValue}
                                                setValue={setQueryVariableValue}
                                                options={
                                                    template.queryVariablesSetter[
                                                        "creator-assignee"
                                                    ].options
                                                }
                                            />
                                        </Grid>
                                    )}
                                <Grid item>
                                    <JiraProjectSelector
                                        queryVariablesSetter={template.queryVariablesSetter}
                                        value={queryVariableProjectValue}
                                        setValue={setQueryVariableProjectValue}
                                    />
                                </Grid>
                                {template.propertySetter &&
                                    template.propertySetter.type === "switch" && (
                                        <Grid item>
                                            <TemplateSwitch
                                                value={propertyValue}
                                                setValue={setPropertyValue}
                                                options={template.propertySetter.options}
                                            />
                                        </Grid>
                                    )}
                            </Grid>
                        </Card>
                    </Grid>
                    {getRows(1)}
                </>
            );
        }
        return getRows();
    };

    return (
        <Grid container direction="column" style={{ width: "100%" }}>
            {getSection()}
        </Grid>
    );
};

export default Template;
